import { db } from './firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  getDocs,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  limit
} from 'firebase/firestore';

/**
 * Create a new handicapper pick
 * @param {Object} pickData - The pick data
 * @param {string} pickData.handicapperId - UID of the handicapper
 * @param {string} pickData.handicapperName - Display name of the handicapper
 * @param {string} pickData.gameId - The game/event ID
 * @param {string} pickData.sport - Sport type (e.g., 'basketball_nba')
 * @param {string} pickData.homeTeam - Home team name
 * @param {string} pickData.awayTeam - Away team name
 * @param {string} pickData.pickType - Type of pick (h2h, spreads, totals, etc.)
 * @param {string} pickData.pickedTeam - The team/outcome picked
 * @param {number} pickData.odds - The odds at time of pick
 * @param {string} pickData.confidence - Confidence level (low, medium, high)
 * @param {string} pickData.analysis - Handicapper's analysis text
 * @param {string} pickData.bookmaker - Sportsbook name
 * @param {string} pickData.commenceTime - Game commence time
 * @returns {Promise<string>} The created pick document ID
 */
export const createPick = async (pickData) => {
  try {
    const pick = {
      ...pickData,
      result: 'pending', // 'pending', 'won', 'lost', 'push'
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'handicapperPicks'), pick);
    console.log('Pick created with ID:', docRef.id);
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating pick:', error);
    throw error;
  }
};

/**
 * Get all picks by a specific handicapper
 * @param {string} handicapperId - The handicapper's UID
 * @param {number} limitCount - Maximum number of picks to return (default: 50)
 * @returns {Promise<Array>} Array of pick documents
 */
export const getPicksByHandicapper = async (handicapperId, limitCount = 50) => {
  try {
    const q = query(
      collection(db, 'handicapperPicks'),
      where('handicapperId', '==', handicapperId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const picks = [];
    
    querySnapshot.forEach((doc) => {
      picks.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return picks;
  } catch (error) {
    console.error('Error getting picks by handicapper:', error);
    throw error;
  }
};

/**
 * Get all picks for a specific handicapper by their display name
 * @param {string} handicapperName - The handicapper's display name
 * @param {number} limitCount - Maximum number of picks to return (default: 50)
 * @returns {Promise<Array>} Array of pick documents
 */
export const getPicksByHandicapperName = async (handicapperName, limitCount = 50) => {
  try {
    const q = query(
      collection(db, 'handicapperPicks'),
      where('handicapperName', '==', handicapperName),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const picks = [];
    
    querySnapshot.forEach((doc) => {
      picks.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return picks;
  } catch (error) {
    console.error('Error getting picks by handicapper name:', error);
    throw error;
  }
};

/**
 * Get all picks for a specific handicapper by their URL slug (firstname-lastname)
 * @param {string} handicapperSlug - The handicapper's URL slug (e.g., 'john-doe')
 * @param {number} limitCount - Maximum number of picks to return (default: 50)
 * @returns {Promise<Array>} Array of pick documents
 */
export const getPicksByHandicapperSlug = async (handicapperSlug, limitCount = 50) => {
  try {
    const q = query(
      collection(db, 'handicapperPicks'),
      where('handicapperSlug', '==', handicapperSlug),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const picks = [];
    
    querySnapshot.forEach((doc) => {
      picks.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return picks;
  } catch (error) {
    console.error('Error getting picks by handicapper slug:', error);
    throw error;
  }
};

/**
 * Get recent picks across all handicappers
 * @param {number} limitCount - Maximum number of picks to return (default: 20)
 * @returns {Promise<Array>} Array of pick documents
 */
export const getRecentPicks = async (limitCount = 20) => {
  try {
    const q = query(
      collection(db, 'handicapperPicks'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const picks = [];
    
    querySnapshot.forEach((doc) => {
      picks.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return picks;
  } catch (error) {
    console.error('Error getting recent picks:', error);
    throw error;
  }
};

/**
 * Update the result of a pick
 * @param {string} pickId - The pick document ID
 * @param {string} result - The result ('won', 'lost', 'push')
 * @returns {Promise<void>}
 */
export const updatePickResult = async (pickId, result) => {
  try {
    const validResults = ['won', 'lost', 'push', 'pending'];
    
    if (!validResults.includes(result)) {
      throw new Error(`Invalid result: ${result}. Must be one of: ${validResults.join(', ')}`);
    }
    
    const pickRef = doc(db, 'handicapperPicks', pickId);
    await updateDoc(pickRef, {
      result,
      updatedAt: serverTimestamp()
    });
    
    console.log(`Pick ${pickId} result updated to: ${result}`);
  } catch (error) {
    console.error('Error updating pick result:', error);
    throw error;
  }
};

/**
 * Mark notifications as sent for a pick
 * @param {string} pickId - The pick document ID
 * @returns {Promise<void>}
 */
export const markNotificationsSent = async (pickId) => {
  try {
    const pickRef = doc(db, 'handicapperPicks', pickId);
    await updateDoc(pickRef, {
      notificationsSent: true,
      updatedAt: serverTimestamp()
    });
    
    console.log(`Notifications marked as sent for pick: ${pickId}`);
  } catch (error) {
    console.error('Error marking notifications sent:', error);
    throw error;
  }
};

/**
 * Get statistics for a handicapper by name
 * @param {string} handicapperName - The handicapper's display name
 * @returns {Promise<Object>} Statistics object with wins, losses, pushes, win rate
 */
export const getHandicapperStatsByName = async (handicapperName) => {
  try {
    const picks = await getPicksByHandicapperName(handicapperName, 1000);
    
    const stats = {
      totalPicks: picks.length,
      wins: picks.filter(p => p.result === 'won').length,
      losses: picks.filter(p => p.result === 'lost').length,
      pushes: picks.filter(p => p.result === 'push').length,
      pending: picks.filter(p => p.result === 'pending').length,
      winRate: 0
    };
    
    // Calculate win rate (excluding pending and pushes)
    const decidedPicks = stats.wins + stats.losses;
    if (decidedPicks > 0) {
      stats.winRate = (stats.wins / decidedPicks * 100).toFixed(1);
    }
    
    return stats;
  } catch (error) {
    console.error('Error getting handicapper stats by name:', error);
    throw error;
  }
};

/**
 * Get statistics for a handicapper by URL slug
 * @param {string} handicapperSlug - The handicapper's URL slug (e.g., 'john-doe')
 * @returns {Promise<Object>} Statistics object with wins, losses, pushes, win rate
 */
export const getHandicapperStatsBySlug = async (handicapperSlug) => {
  try {
    const picks = await getPicksByHandicapperSlug(handicapperSlug, 1000);
    
    const stats = {
      totalPicks: picks.length,
      wins: picks.filter(p => p.result === 'won').length,
      losses: picks.filter(p => p.result === 'lost').length,
      pushes: picks.filter(p => p.result === 'push').length,
      pending: picks.filter(p => p.result === 'pending').length,
      winRate: 0
    };
    
    // Calculate win rate (excluding pending and pushes)
    const decidedPicks = stats.wins + stats.losses;
    if (decidedPicks > 0) {
      stats.winRate = (stats.wins / decidedPicks * 100).toFixed(1);
    }
    
    return stats;
  } catch (error) {
    console.error('Error getting handicapper stats by slug:', error);
    throw error;
  }
};

/**
 * Get statistics for a handicapper
 * @param {string} handicapperId - The handicapper's UID
 * @returns {Promise<Object>} Statistics object with wins, losses, pushes, win rate
 */
export const getHandicapperStats = async (handicapperId) => {
  try {
    const picks = await getPicksByHandicapper(handicapperId, 1000); // Get all picks
    
    const stats = {
      totalPicks: picks.length,
      wins: picks.filter(p => p.result === 'won').length,
      losses: picks.filter(p => p.result === 'lost').length,
      pushes: picks.filter(p => p.result === 'push').length,
      pending: picks.filter(p => p.result === 'pending').length,
      winRate: 0
    };
    
    // Calculate win rate (excluding pending and pushes)
    const decidedPicks = stats.wins + stats.losses;
    if (decidedPicks > 0) {
      stats.winRate = (stats.wins / decidedPicks * 100).toFixed(1);
    }
    
    return stats;
  } catch (error) {
    console.error('Error getting handicapper stats:', error);
    throw error;
  }
};

/**
 * Notify followers about a new pick
 * This is a placeholder for future notification implementation
 * @param {string} pickId - The pick document ID
 * @param {string} handicapperId - The handicapper's UID
 * @returns {Promise<void>}
 */
export const notifyFollowers = async (pickId, handicapperId) => {
  try {
    // TODO: Implement notification logic
    // 1. Query users who follow this handicapper
    // 2. Check their notification preferences (userSettings.pushNotifications)
    // 3. Send push notifications
    // 4. Mark notifications as sent
    
    console.log(`Notification triggered for pick ${pickId} by handicapper ${handicapperId}`);
    
    // For now, do nothing - notifications are not yet implemented
  } catch (error) {
    console.error('Error notifying followers:', error);
    // Don't throw - this is not critical
  }
};
